import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from "react-router";
import i18n, {
  authenticatedLanguageStorageKey,
  manualLanguageOverrideStorageKey,
  normalizeSupportedLanguage,
} from 'i18n';
import Loading from './shared/loading/Loading'
import { Authentication, } from 'shared/topchef.types';
import api from 'shared/topchef-api-places';
import './App.css';
import UnknownUser from './shared/unknown-user/UnknownUser';
import { AnalyticsProvider } from "@keiko-app/react-google-analytics";
import { useAnalytics } from "@keiko-app/react-google-analytics";

export const authenticatedCountryStorageKey = 'topchefAuthenticatedCountry';

const analyticsConfig = {
    measurementId: "G-0VVTD2FT33"
};

const getBrowserLanguage = (): string | undefined => {
  const browserLanguage = window.navigator.languages?.[0] ?? window.navigator.language;
  return normalizeSupportedLanguage(browserLanguage);
};

const getLanguageFromCountry = (country?: string | null): string | undefined => {
  const normalizedCountry = country?.toLowerCase().trim();

  if (!normalizedCountry) {
    return undefined;
  }

  const countryToLanguageMap: Record<string, string> = {
    france: 'fr',
    spain: 'es',
  };

  return countryToLanguageMap[normalizedCountry];
};

const getAuthPreferredLanguage = (authLanguage?: string | null, authCountry?: string | null): string | undefined => {
  const languageFromAuth = normalizeSupportedLanguage(authLanguage);
  if (languageFromAuth) {
    return languageFromAuth;
  }

  return getLanguageFromCountry(authCountry);
};

const AppContent = () => {
  const [isAuthenticated, setAuthenticated] = useState<boolean>(false);
  const [authenticationFailed, setAuthenticationFailed] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { tracker } = useAnalytics();

  // ⬇ Track page views à chaque changement de route
  useEffect(() => {
    tracker.trackPageView({});
    
    // ⬇ Envoi d'un faux événement pour tester Analytics
    tracker.trackEvent('TestEvent', {
      category: 'Test',
      label: 'AppContent mounted',
      value: 1
    });

  }, [location, tracker]);

  useEffect(() => {
    if (isAuthenticated || authenticationFailed) return;

    async function authenticate() {
      try {
        const auth: Authentication = await api.authenticate();
        if (auth.country) {
          localStorage.setItem(authenticatedCountryStorageKey, auth.country);
        }

        if (auth.type === "place") {
          const placeLanguage = getAuthPreferredLanguage(auth.language, auth.country);

          if (placeLanguage && i18n.resolvedLanguage !== placeLanguage) {
            await i18n.changeLanguage(placeLanguage);
          }

          navigate('/places');
          setAuthenticated(true);
          return;
        }

        if (auth.type !== "backoffice") {
          throw new Error("type d'utilisateur inconnu");
        }

        const hasManualOverride = localStorage.getItem(manualLanguageOverrideStorageKey) === 'true';
        const manualLanguage = normalizeSupportedLanguage(localStorage.getItem(authenticatedLanguageStorageKey));

        if (hasManualOverride && manualLanguage) {
          if (i18n.resolvedLanguage !== manualLanguage) {
            await i18n.changeLanguage(manualLanguage);
          }

          navigate('/backoffice');
          setAuthenticated(true);
          return;
        }

        localStorage.removeItem(manualLanguageOverrideStorageKey);
        const preferredLanguage = getAuthPreferredLanguage(auth.language, auth.country);

        if (preferredLanguage && i18n.resolvedLanguage !== preferredLanguage) {
          await i18n.changeLanguage(preferredLanguage);
        } else if (!preferredLanguage) {
          localStorage.removeItem(authenticatedLanguageStorageKey);

          const browserLanguage = getBrowserLanguage();
          if (browserLanguage && i18n.resolvedLanguage !== browserLanguage) {
            await i18n.changeLanguage(browserLanguage);
            localStorage.removeItem(authenticatedLanguageStorageKey);
          }
        }

        navigate('/backoffice');
        setAuthenticated(true);
      } catch (error: any) {
        setAuthenticationFailed(true);
      }
    }

    authenticate();
  }, [isAuthenticated, authenticationFailed, navigate]);

  if (authenticationFailed) return <UnknownUser />;
  return <Loading />;
};

const App = () => {
  return (
    <AnalyticsProvider config={analyticsConfig}>
      <AppContent />
    </AnalyticsProvider>
  );
};

export default App;
